import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { consola } from 'consola'
import { useBasketState } from '../useBasketState'
import { useCheckoutFormStore } from '../useCheckoutFormStore'
import { pickDeliveryCoords } from '../../lib/deliveryCoords'
import { apiOrder, useCartStore } from '@entities/order'
import type { CalculateOrderInput, CalculateOrderResponse, OrderItemInput } from '@entities/order'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import { useAuthState } from '~/entities/auth'
import { useKitToast } from '@shared/ui/kit'
import { modalStateEnum, useModalStore } from '@shared/model'

const logger = consola.withTag('order-preview')

/** Код ошибки бэкенда: выбрана доставка, но адрес (зона) не указан. */
const DELIVERY_ADDRESS_REQUIRED = 'DELIVERY_ADDRESS_REQUIRED'
/** Адрес вне всех зон доставки — сервер отвечает текстом для гостя. */
const DELIVERY_OUT_OF_ZONE = 'DELIVERY_OUT_OF_ZONE'
/**
 * Ошибки сервера про адрес, которые чинятся только на карте: показываем тост с кнопкой
 * «Указать на карте», а не молча пустую сумму.
 */
const ADDRESS_MAP_ERROR_CODES = new Set([
  DELIVERY_ADDRESS_REQUIRED,
  DELIVERY_OUT_OF_ZONE,
  'DELIVERY_COORDS_REQUIRED',
  'DELIVERY_ADDRESS_INCOMPLETE',
  'DELIVERY_ADDRESS_UNCONFIRMED',
])

/**
 * Коды, при которых сервер забраковал саму ТОЧКУ (координаты филиала, центр
 * населённого пункта, адрес не подтверждается рядом с точкой). Перед открытием карты
 * такую точку снимаем с выбранного адреса, иначе карта откроется на ней же и отказ
 * повторится: отличить испорченную запись от настоящей на клиенте нечем.
 */
const STALE_POINT_ERROR_CODES = new Set([
  'DELIVERY_COORDS_REQUIRED',
  'DELIVERY_ADDRESS_INCOMPLETE',
  'DELIVERY_ADDRESS_UNCONFIRMED',
])

/**
 * ✅ PREVIEW-расчёт заказа на бэкенде (POST /orders/calculate).
 *
 * Единый источник истины по деньгам: сервисный сбор, итог, начисляемые баллы и
 * приветственная скидка считаются на сервере той же формулой, что и при создании
 * заказа. Фронт показывает эти цифры, а локальный calculateTotalPrice остаётся лишь
 * оптимистичным плейсхолдером, пока ответ не пришёл.
 *
 * Запрос дебаунсится и отправляется при изменении состава корзины, баллов, промокода
 * и параметров доставки. Работает только на клиенте и только для авторизованных.
 *
 * Pinia-store (синглтон): watcher и дебаунс живут в одном экземпляре, поэтому
 * вызов из разных composable'ов не плодит дублирующие запросы.
 */
export const useOrderPreview = defineStore('orderPreview', () => {
  const cartStore = useCartStore()
  const basketState = useBasketState()
  const checkoutFormStore = useCheckoutFormStore()
  const institution = useInstitutionStore()
  const userProfile = useUserProfileStore()
  const { isLoggedIn } = useAuthState()
  const { toast, remove } = useKitToast()
  const modalStore = useModalStore()

  const preview = ref<CalculateOrderResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let requestSeq = 0

  // ── Тост «укажите адрес доставки» с кнопкой открытия карты ──
  // Preview пересчитывается часто, поэтому держим единственный (persistent) тост и
  // дедупим по id, чтобы не спамить на каждом фоновом расчёте.
  let addressToastId: number | null = null

  /** Код последней ошибки адреса — решает, снимать ли точку при открытии карты. */
  let lastAddressErrorCode: string | null = null

  function openAddressMap(): void {
    addressToastId = null // тост закрывается при действии — снимаем дедуп
    if (lastAddressErrorCode && STALE_POINT_ERROR_CODES.has(lastAddressErrorCode)) {
      checkoutFormStore.setDeliveryCoords(null)
      void userProfile.dropSelectedAddressCoords()
    }
    modalStore.open(modalStateEnum.ModalMapYandex, { from: 'checkout', orderType: 'delivery' })
  }

  function showAddressToast(message: string, title = 'Укажите адрес доставки'): void {
    if (addressToastId !== null) return
    addressToastId = toast({
      color: 'warning',
      title,
      description: message,
      timeout: 0, // висит, пока адрес не выбран или пользователь не закрыл
      action: { label: 'Указать на карте', onClick: openAddressMap },
    })
  }

  function clearAddressToast(): void {
    if (addressToastId !== null) {
      remove(addressToastId)
      addressToastId = null
    }
  }

  /** Маппинг позиций корзины в OrderItemInput (зеркало submitForm). */
  function buildItems(): OrderItemInput[] {
    return cartStore.cart.map((item: any) => {
      if (item.itemType === 'combo') {
        return {
          itemType: 'combo',
          comboId: String(item.comboId),
          quantity: item.quantity,
          comboSelections: item.comboSelections || [],
        }
      }
      if (item.itemType === 'product') {
        return {
          itemType: 'product',
          productId: String(item.productId),
          quantity: item.quantity,
          addons: item.addons?.length ? item.addons.map(String) : undefined,
          excludes: item.excludes?.length ? item.excludes.map(String) : undefined,
        }
      }
      return {
        productId: String(item.id),
        quantity: item.quantity,
        addons: item.addons?.length ? item.addons.map(String) : undefined,
        excludes: item.excludes?.length ? item.excludes.map(String) : undefined,
      }
    })
  }

  /**
   * Зона доставки для запроса: сперва из state корзины (выбрана в этой сессии),
   * иначе — из дефолтного адреса профиля (восстановлен из localStorage/куков).
   * Зеркалит логику submitForm, чтобы preview не слал запрос без зоны, когда
   * дефолтный адрес уже подтянулся.
   */
  function resolveDeliveryZoneId(): string | undefined {
    if (basketState.deliveryZoneId) return String(basketState.deliveryZoneId)
    const zona = userProfile.selectedDeliveryAddress?.zona
    return zona == null ? undefined : String(zona)
  }

  /**
   * Координаты адреса доставки — те же, что уйдут в заказ (pickDeliveryCoords): по ним бэк
   * резолвит филиал/зону и котирует Яндекс Доставку, иначе preview и create разошлись бы в цене.
   */
  function resolveDeliveryCoords() {
    return pickDeliveryCoords(
      userProfile.selectedDeliveryAddress,
      checkoutFormStore.deliveryCoords,
      institution.branches,
    )
  }

  function buildRequest(): CalculateOrderInput | null {
    const userId = userProfile.getUser?.id
    const branchId = institution.selectedBranch?.id
    const institutionId = institution.institution?.id
    if (!userId || !branchId || !institutionId || cartStore.cart.length === 0) {
      return null
    }

    const methodReceipt = checkoutFormStore.getForm.method_receipt as
      | 'pickup'
      | 'delivery'
      | 'dine_in'
    const orderType = (methodReceipt || 'takeaway') as CalculateOrderInput['orderType']

    // Баллы к списанию шлём только если пользователь выбрал режим баллов.
    // pointsToUse === 0 → бэк применит приветственную скидку (дефолтный сценарий).
    const pointsToUse = basketState.useLoyaltyPoints ? basketState.pointsToUse : 0
    const isDelivery = methodReceipt === 'delivery'
    const deliveryCoords = isDelivery ? resolveDeliveryCoords() : null

    return {
      userId: String(userId),
      branchId: String(branchId),
      institutionId: String(institutionId),
      orderType,
      items: buildItems(),
      pointsToUse: pointsToUse || undefined,
      promocodeId: basketState.appliedPromocodeId || undefined,
      deliveryZoneId: isDelivery ? resolveDeliveryZoneId() : undefined,
      deliveryLatitude: deliveryCoords?.latitude,
      deliveryLongitude: deliveryCoords?.longitude,
      methodReceipt,
    }
  }

  /**
   * Бэкенд запретил списание (сумма ниже порога / заказ раньше N-го) — снимаем уже
   * выбранные баллы. Иначе корзина показывала бы списание, а оформление падало бы
   * ошибкой LOYALTY_MIN_ORDER_AMOUNT / LOYALTY_MIN_PAID_ORDERS.
   *
   * Сброс меняет входные данные расчёта, поэтому watcher запустит его заново — уже
   * без баллов, и повторно сюда не зайдёт (условие перестанет выполняться).
   */
  function resetPointsIfRedeemBlocked(result: CalculateOrderResponse): void {
    const redeem = result.pointsRedeem
    if (!redeem || redeem.available) return

    const blocking = ['min_order_amount', 'min_paid_orders', 'promocode_incompatible']
    if (!redeem.reason || !blocking.includes(redeem.reason)) return
    if (!basketState.useLoyaltyPoints && basketState.pointsToUse === 0) return

    logger.info('Списание баллов недоступно, снимаем выбранные баллы:', redeem.reason)
    basketState.toggleLoyaltyPoints(false)
  }

  /**
   * Состав корзины уже относится к тому филиалу, для которого считаем.
   * Один и тот же гейт нужен и механике подарков, поэтому живёт в сторе корзины:
   * до конца переезда расчёт ушёл бы с новым branch_id по старому составу, и бэк
   * честно отвечал PRODUCT_UNAVAILABLE — пользователь видел ложную ошибку.
   */
  function isCartSettledForBranch(): boolean {
    return cartStore.isSettledForBranch
  }

  async function fetchPreview(): Promise<void> {
    if (!import.meta.client || !isLoggedIn.value) return

    // Ждём, пока корзина догонит выбранный филиал; вотчер ниже перезапустит расчёт.
    if (!isCartSettledForBranch()) {
      logger.debug('Расчёт отложен: корзина ещё переезжает на другой филиал')
      return
    }

    const request = buildRequest()
    if (!request) {
      preview.value = null
      clearAddressToast()
      return
    }

    // Доставка выбрана, но зоны (адреса) в запросе нет.
    if (request.methodReceipt === 'delivery' && !request.deliveryZoneId) {
      // Не дёргаем /orders/calculate, пока дефолтный адрес не подтянулся из хранилища:
      // иначе уходит заведомо неполный запрос (без зоны) → бэк отвечает «укажите адрес»,
      // а через ~2с адрес восстанавливается и ошибка снимается — это и есть мигающий тост.
      // Дождёмся восстановления; watcher ниже перезапустит расчёт по флагу.
      if (!userProfile.isDeliveryAddressRestored) return

      // Адрес не выбран — пробуем молча подставить избранный (дефолтный) адрес из
      // профиля (/users/addresses уже загружены при логине, лишнего запроса нет).
      // Если получилось — watcher перезапустит расчёт уже с зоной, тост не нужен.
      if (await userProfile.ensureDefaultDeliveryAddress()) return

      // Избранного адреса нет: не шлём обречённый запрос (он бы возвращал
      // DELIVERY_ADDRESS_REQUIRED на каждом фоновом пересчёте — это и «бесит»).
      // Показываем единственный (дедуплицированный) тост с кнопкой выбора на карте.
      preview.value = null
      error.value = 'Укажите адрес доставки'
      showAddressToast('Укажите адрес доставки, чтобы рассчитать стоимость заказа.')
      return
    }

    const seq = ++requestSeq
    loading.value = true
    error.value = null
    try {
      const result = await apiOrder.calculateOrder(request)
      // Игнорируем устаревшие ответы (пришёл более новый запрос).
      if (seq === requestSeq) {
        preview.value = result
        lastAddressErrorCode = null
        clearAddressToast()
        resetPointsIfRedeemBlocked(result)
      }
    } catch (error_: any) {
      if (seq === requestSeq) {
        const code = error_?.response?._data?.code
        const message = error_?.response?._data?.message
        error.value = message || error_?.message || 'Ошибка расчёта'
        logger.warn('Не удалось получить preview-расчёт:', error.value)

        // Частый и «бесящий» кейс: выбрана доставка, но адрес не указан. Сюда же —
        // адрес вне зон, без номера дома или без точки: сервер уже сказал гостю,
        // что не так, а кнопка тоста ведёт на карту, где это чинится.
        if (ADDRESS_MAP_ERROR_CODES.has(code)) {
          lastAddressErrorCode = code
          showAddressToast(
            error.value,
            code === DELIVERY_OUT_OF_ZONE ? 'Доставка недоступна' : 'Укажите адрес доставки',
          )
        } else {
          lastAddressErrorCode = null
          clearAddressToast()
        }
      }
    } finally {
      if (seq === requestSeq) loading.value = false
    }
  }

  function scheduleFetch(): void {
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(fetchPreview, 250)
  }

  // Пересчёт при изменении значимых для суммы входных данных.
  watch(
    () => ({
      cartTotal: basketState.cartTotal,
      itemsCount: basketState.cartItemsCount,
      useLoyaltyPoints: basketState.useLoyaltyPoints,
      pointsToUse: basketState.pointsToUse,
      promocodeId: basketState.appliedPromocodeId,
      deliveryZoneId: basketState.deliveryZoneId,
      methodReceipt: checkoutFormStore.getForm.method_receipt,
      loggedIn: isLoggedIn.value,
      // Перезапускаем расчёт, когда дефолтный адрес восстановился из хранилища
      // (и когда подтянулась его зона) — см. гейт в fetchPreview.
      addressRestored: userProfile.isDeliveryAddressRestored,
      deliveryZona: userProfile.selectedDeliveryAddress?.zona,
      // Смена адреса внутри той же зоны меняет котировку Яндекса — отслеживаем и
      // координаты, а не только зону.
      mapLatitude: checkoutFormStore.deliveryCoords?.latitude,
      mapLongitude: checkoutFormStore.deliveryCoords?.longitude,
      addressLatitude: userProfile.selectedDeliveryAddress?.latitude,
      addressLongitude: userProfile.selectedDeliveryAddress?.longitude,
      // Филиал расчёта и филиал, к которому уже относится корзина: пока они
      // расходятся, расчёт откладывается (см. isCartSettledForBranch), а как
      // только корзина переехала — вотчер запустит его снова.
      branchId: institution.selectedBranch?.id,
      cartBranchId: cartStore.selectedBranchId,
      cartLoading: cartStore.isLoading,
    }),
    scheduleFetch,
    { deep: true, immediate: true },
  )

  /** Активный сценарий: баллы — если выбраны и списываются, иначе приветственная скидка. */
  const activeScenario = computed(() => {
    if (!preview.value) return null
    const usePoints = basketState.useLoyaltyPoints && basketState.pointsToUse > 0
    return usePoints ? preview.value.scenarios.points : preview.value.scenarios.onboarding
  })

  return {
    preview,
    loading,
    error,
    activeScenario,
    refresh: fetchPreview,
  }
})
